import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const ROW_COUNT = 7;

/**
 * Table-shaped loader: grey tracks with a colored pin sweep, plus a soft
 * primary scan that travels down the list. Used while the first scores load.
 */
export default function RelativeStrengthSkeletonRows({
  count = ROW_COUNT,
  gridTemplateColumns = 'minmax(320px, 1fr) 72px 100px 72px',
  gap = 14,
}) {
  const { colors } = useTheme();
  const accent = colors.primary || '#7c3aed';
  const track = colors.border || 'rgba(255,255,255,0.12)';

  return (
    <div
      data-testid="relative-strength-skeleton"
      aria-hidden="true"
      style={{ position: 'relative', overflow: 'hidden' }}
    >
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          style={{
            display: 'grid',
            gridTemplateColumns,
            alignItems: 'center',
            gap,
            padding: '13px 12px',
            borderBottom: `1px solid ${colors.border}`,
            animation: 'rs-row-fade 1.8s ease-in-out infinite',
            animationDelay: `${index * 90}ms`,
          }}
        >
          <span
            style={{
              display: 'block',
              width: `${52 + ((index * 13) % 28)}%`,
              height: 11,
              borderRadius: 4,
              backgroundColor: colors.bgSecondary || track,
            }}
          />
          <span
            style={{
              position: 'relative',
              height: 4,
              borderRadius: 999,
              backgroundColor: track,
              overflow: 'visible',
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: -4,
                width: 10,
                height: 10,
                borderRadius: 999,
                backgroundColor: accent,
                boxShadow: `0 0 8px ${accent}99`,
                animation: 'rs-pin-travel 2.2s ease-in-out infinite',
                animationDelay: `${index * 120}ms`,
              }}
            />
          </span>
          <span
            style={{
              display: 'block',
              width: 28,
              height: 11,
              marginLeft: 'auto',
              borderRadius: 4,
              backgroundColor: colors.bgSecondary || track,
            }}
          />
        </div>
      ))}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          height: 56,
          pointerEvents: 'none',
          background: `linear-gradient(180deg, transparent, ${accent}22, transparent)`,
          animation: 'rs-scan-down 2.4s ease-in-out infinite',
        }}
      />
      <style>
        {`
          @keyframes rs-pin-travel {
            0% { left: 8%; opacity: 0.55; }
            50% { left: 78%; opacity: 1; }
            100% { left: 8%; opacity: 0.55; }
          }
          @keyframes rs-scan-down {
            0% { top: -56px; opacity: 0; }
            18% { opacity: 1; }
            82% { opacity: 1; }
            100% { top: 100%; opacity: 0; }
          }
          @keyframes rs-row-fade {
            0%, 100% { opacity: 0.55; }
            50% { opacity: 1; }
          }
        `}
      </style>
    </div>
  );
}
